<template>
  <component :is="tag">
    <slot
      :all-fields="allFields"
      :filters-list="filtersList"
      :default-fields-list="defaultFieldsList"
      :set-offer-field="setOfferField" />
  </component>
</template>

<script>
import CurrencyFilterField from 'Root/classes/CurrencyFilterField';
import CurrencyDefaultFieldsModel from 'Models/CurrencyDefaultFieldsModel';
import { currencyGetPriceWithCommision } from 'Root/helpers/currency';
import CurrencyFilterModel from 'Models/CurrencyFilterModel';
import CurrencyOfferModel from 'Models/CurrencyOfferModel';

export default {
  name: 'BaseCurrencyOffer',

  props: {
    currencySettings: {
      type: Object,
      required: true,
    },

    offerData: {
      type: Object,
      default: () => ({}),
    },

    currencyFieldType: {
      type: String,
      default: 'component_view',
      validate: (type) => ['component_view', 'component_edit'].includes(type),
    },

    shouldSetDefaultValues: {
      type: Boolean,
      default: false,
    },

    shouldInitDefaultFilters: {
      type: Boolean,
      default: true,
    },

    tag: {
      type: String,
      default: 'div',
    },
  },

  data() {
    return {
      initialOfferData: JSON.parse(JSON.stringify(this.offerData)),
    };
  },

  computed: {
    parsedOfferValues() {
      if (!this.offerData?.data || !Object.keys(this.offerData.data).length) {
        return {};
      }

      const offerValues = {};

      this.currencySettings.product_currency_setting_fields.forEach((field) => {
        if (!this.offerData.data[field.id]) {
          return;
        }

        const valueInfo = field.product_currency_setting_field_values
          .find((value) => value.value === this.offerData.data[field.id]);

        if (valueInfo) {
          offerValues[field.id] = {
            id: valueInfo.id,
            value: valueInfo.value,
          };
        } else {
          offerValues[field.id] = this.offerData.data[field.id];
        }
      });

      return offerValues;
    },

    filtersList() {
      const fields = [];
      const fieldsToAdd = {};

      if (this.currencySettings?.product_currency_setting_fields === undefined) {
        return fields;
      }

      this.currencySettings.product_currency_setting_fields?.forEach((field) => {
        const newField = { ...field };

        if (this.parsedOfferValues?.[newField.id]) {
          if (field.component_edit === CurrencyFilterModel.ID_BY_FIELD.INPUT) {
            newField.default_value = this.parsedOfferValues[newField.id];
          } else {
            newField.default_value = {
              id: this.parsedOfferValues[newField.id].id,
              value: this.parsedOfferValues[newField.id].value,
            };
          }
        } else if (this.shouldSetDefaultValues && newField.default_value_id) {
          const defaultValue = newField.product_currency_setting_field_values
            .find((fieldValue) => fieldValue.id === newField.default_value_id);

          if (defaultValue) {
            newField.default_value = {
              id: defaultValue.id,
              value: defaultValue.value,
            };
          }
        }

        if (newField.parent_id && newField.parent_value_id) {
          let parent = fieldsToAdd[newField.parent_id];

          if (!parent) {
            parent = {
              _children: {},
            };

            fieldsToAdd[newField.parent_id] = parent;
          } else if (!('_children' in parent)) {
            parent._children = {};
          }

          if (
            // если в значениях выбран родительский фильтр = нужному для этого ребёнка - запихиваем его
            // перебиваем поле независимо есть там что-то или нет
            this.parsedOfferValues[newField.parent_id]?.id === newField.parent_value_id
            // если никем не занято то запихиваем что б было
            || !parent._children[newField.title]
          ) {
            // группируем по title, что б не показывать 2 одинаковых поля
            parent._children[newField.title] = {
              ...newField,
              _disabled: !(parent.default_value || (this.shouldSetDefaultValues && parent.default_value_id)),
            };
          }
        } else {
          fieldsToAdd[newField.id] = {
            ...fieldsToAdd[newField.id],
            ...newField,
          };
        }
      });

      Object.values(fieldsToAdd).forEach((field) => {
        fields.push(new CurrencyFilterField(field[this.currencyFieldType], field));

        if (field._children) {
          Object.values(field._children).forEach((childField) => {
            fields.push(new CurrencyFilterField(childField[this.currencyFieldType], childField));
          });
        }
      });

      return fields.sort((a, b) => b.fieldData.priority - a.fieldData.priority);
    },

    defaultFieldsList() {
      const fields = [];

      if (!this.shouldInitDefaultFilters) {
        return fields;
      }

      /* SKY-4332 лечение бесконечного обновления компонента */
      JSON.parse(JSON.stringify(CurrencyDefaultFieldsModel)).LIST.forEach((item) => {
        item.data.default_value = this.offerData[item.data._key] !== undefined
          ? this.offerData[item.data._key]
          : null;

        if (item.data._key === CurrencyDefaultFieldsModel.STOCK.data._key) {
          item.data.min = this.currencySettings.step_size;
        }

        if (item.data._key === CurrencyDefaultFieldsModel.SKYCOACH_PRICE.data._key) {
          item.data.default_value = this.skycoachPrice;
        }

        if (item.data._key === CurrencyDefaultFieldsModel.MIN_PURCHASE.data._key) {
          item.data.max = this.offerData[CurrencyDefaultFieldsModel.STOCK.data._key] || 0;
        }

        if (item.data._key === CurrencyDefaultFieldsModel.PRICE_PER_STEP.data._key) {
          item.data.textAppendix.mobilePrefix = item.data.textAppendix.mobilePrefix
            .replace('%STEPSIZE%', this.currencySettings.step_size);
        }

        fields.push(new CurrencyFilterField(item.component_edit, item.data));
      });

      return fields;
    },

    skycoachPrice() {
      const pricePerStep = this.offerData[CurrencyDefaultFieldsModel.PRICE_PER_STEP.data._key];

      if (pricePerStep) {
        return currencyGetPriceWithCommision(pricePerStep, this.currencySettings.commission_percent, 4);
      }

      return null;
    },

    allFields() {
      return [
        ...this.filtersList,
        ...this.defaultFieldsList,
      ];
    },

    offerDataErrorsList() {
      return this.offerData._getOfferDataErrorsList && this.offerData._getOfferDataErrorsList();
    },
  },

  watch: {
    filtersList: {
      handler(newVal, oldVal) {
        const errors = this.offerDataErrorsList;
        if (!errors) {
          return;
        }

        oldVal.some((filter) => {
          if (errors.length) {
            this.$emit('check-duplicate', false);
            return true;
          }

          if (!filter._wasEdited) {
            return false;
          }

          // check for Region and Server fields
          if (!errors.length) {
            this.$emit('check-duplicate', true);
            return true;
          }

          return false;
        });
      },
      deep: true,
    },

    'offerData.status': {
      handler() {
        this.$emit('check-duplicate', !this.offerDataErrorsList?.length);
      },
    },
  },

  created() {
    this.fillOfferData();
    this.createUpdater();
  },

  updated() {
    this.createUpdater();
  },

  methods: {
    createUpdater() {
      if (!this.offerData._getUpdatedOffer) {
        this.$set(this.offerData, '_wasEdited', false);
        // eslint-disable-next-line func-names
        this.$set(this.offerData, '_getUpdatedOffer', function () {
          if (!this._wasEdited) {
            return null;
          }

          const data = {
            price_per_step: this.price_per_step,
            amount: this.amount,
            status: this.status,
            min_purchase_amount: this.min_purchase_amount,
            marketplace_type: this.marketplace_type,
            data: this.data,
          };

          if (this.id) {
            data.id = this.id;
          }

          return data;
        });
      }
    },

    fillOfferData() {
      const offer = {
        id: null,
        product_currency_setting_id: this.currencySettings.id,
        status: CurrencyOfferModel.STATUSES.ACTIVE,
        marketplace_type: CurrencyOfferModel.TYPES.GOLD,
        data: {},
      };

      CurrencyDefaultFieldsModel.LIST.forEach((field) => {
        offer[field.data._key] = null;
      });

      Object.entries(offer).forEach(([key, value]) => {
        if (this.offerData[key] === undefined) {
          this.$set(this.offerData, key, value);
        }
      });

      // ставим дефолт значения во внутреннее состояние
      if (this.shouldSetDefaultValues) {
        this.filtersList.forEach((filter) => {
          if (filter.field.value) {
            this.setOfferField({ field: filter, value: filter.field.value });
          }
        });
      }
    },

    setOfferField({ field, value }) {
      let objectToChange;
      let keyToChange;

      if (field.fieldData._key) {
        objectToChange = this.offerData;
        keyToChange = field.fieldData._key;
      } else {
        objectToChange = this.offerData.data;
        keyToChange = field.fieldData.id;
      }

      // поддержка сброса значения чтоб не слать в апи непонятно что
      if (value === null
        || (field.fieldData[this.currencyFieldType] === CurrencyFilterModel.ID_BY_FIELD.SEARCH_INPUT && value === '')
      ) {
        this.$delete(objectToChange, keyToChange);
      } else {
        this.$set(objectToChange, keyToChange, value);
      }

      if (field.fieldData._children) {
        Object.values(field.fieldData._children).forEach((child) => {
          this.$delete(this.offerData.data, child.id);
        });
      }

      field._wasEdited = value === objectToChange[keyToChange];

      // eslint-disable-next-line vue/no-mutating-props
      this.offerData._wasEdited = true;
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
